import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';
import { useHaptic } from '@/hooks/useHaptic';
import { Mic, MicOff, Loader2, Check, RotateCcw, Wand2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface VoiceRecorderProps {
  onTranscriptionComplete: (result: {
    text: string;
    structured?: {
      clima?: string;
      equipe?: number;
      atividades?: string;
      tags?: string[];
      ocorrencia_critica?: boolean;
      resumo_cliente?: string;
    };
  }) => void;
  disabled?: boolean;
}

export function VoiceRecorder({ onTranscriptionComplete, disabled }: VoiceRecorderProps) {
  const [error, setError] = useState<string | null>(null);
  const haptic = useHaptic();

  const {
    isRecording,
    isTranscribing,
    formattedTime,
    hasRecording,
    startRecording,
    stopRecording,
    transcribeAudio,
    clearRecording,
  } = useVoiceRecorder({
    onTranscriptionComplete: (result) => {
      haptic.success();
      onTranscriptionComplete(result);
      clearRecording();
    },
    onError: (err) => {
      haptic.error();
      setError(err);
    },
  });

  const handleMicClick = async () => {
    setError(null);
    if (isRecording) {
      haptic.impact();
      stopRecording();
    } else {
      haptic.tap();
      await startRecording();
    }
  };

  const handleTranscribe = async () => {
    setError(null);
    haptic.tap();
    await transcribeAudio();
  };

  return (
    <Card className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800">
      <div className="flex flex-col items-center">
        {/* Title */}
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium text-violet-800 dark:text-violet-200">
            Diário por Voz
          </span>
        </div>

        {/* Recording Button */}
        <div className="relative mb-3">
          <AnimatePresence>
            {isRecording && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1.3, opacity: 0.3 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="absolute inset-0 rounded-full bg-red-500"
              />
            )}
          </AnimatePresence>

          <Button
            variant={isRecording ? 'destructive' : 'default'}
            size="lg"
            className={cn(
              "relative h-16 w-16 rounded-full",
              isRecording && "bg-red-500 hover:bg-red-600",
              !isRecording && "bg-violet-600 hover:bg-violet-700"
            )}
            onClick={handleMicClick}
            disabled={disabled || isTranscribing}
          >
            {isRecording ? (
              <MicOff className="h-6 w-6" />
            ) : (
              <Mic className="h-6 w-6" />
            )}
          </Button>
        </div>

        {/* Recording Time */}
        {isRecording && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <Badge variant="destructive" className="text-lg px-3 py-1">
              🔴 {formattedTime}
            </Badge>
          </motion.div>
        )}

        {/* Instructions with Example */}
        {!isRecording && !hasRecording && !isTranscribing && (
          <div className="text-center max-w-[280px] space-y-2">
            <p className="text-xs text-muted-foreground">
              Toque no microfone e descreva seu dia de obra.
            </p>
            <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800">
              <p className="text-[10px] text-violet-700 dark:text-violet-300 font-medium mb-1">
                💡 Exemplo de como falar:
              </p>
              <p className="text-[10px] text-violet-600 dark:text-violet-400 italic">
                "Hoje estava <strong>ensolarado</strong>, <strong>5 pessoas</strong> na obra.
                Fizemos a <strong>concretagem do piso</strong> e instalação de caixas elétricas.
                Tivemos um <strong>problema com entrega de material</strong>."
              </p>
            </div>
            <p className="text-[10px] text-muted-foreground/70">
              A IA extrai automaticamente: clima, equipe, atividades e ocorrências
            </p>
          </div>
        )}

        {/* After Recording Actions */}
        {hasRecording && !isTranscribing && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={clearRecording}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Regravar
            </Button>
            <Button
              size="sm"
              onClick={handleTranscribe}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Wand2 className="h-4 w-4 mr-1" />
              Transcrever
            </Button>
          </motion.div>
        )}

        {/* Transcribing State */}
        {isTranscribing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2 text-violet-600"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Transcrevendo com IA...</span>
          </motion.div>
        )}

        {/* Error */}
        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-destructive mt-2 text-center"
          >
            {error}
          </motion.p>
        )}
      </div>
    </Card>
  );
}
